// Animated, screenshot-free product mockups for the /flow landing page.
// Pure CSS/SVG so they stay crisp and lightweight. Keyframes are injected once
// via FlowMockupStyles (rendered a single time by the page).
//
// Palette is built around the LingoChatAI brand blue (--color-primary #006bff):
// deep-blue cards, primary accents, gradients, and rings.

import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Brand-aligned surface colors used across the mockups.
const CARD = '#0a2a52' // deep brand-blue card
const PANEL = '#123a66' // lighter panel / chat bubble
const PANEL_DARK = '#08213f' // darker inner panel (correction card)
const LIGHT = '#eaf3ff' // light listening circle
const ACCENT = '#5aa9ff' // light-blue accent text on dark cards

const FLOW_MOCKUP_KEYFRAMES = `
@keyframes flow-pulse-ring {
  0% { transform: scale(0.85); opacity: 0.6; }
  70% { transform: scale(1.7); opacity: 0; }
  100% { opacity: 0; }
}
@keyframes flow-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
}
@keyframes flow-typing {
  0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-2px); }
}
@keyframes flow-slide-in {
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes flow-wave {
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
}
@keyframes flow-pop {
  0% { transform: scale(0.4); opacity: 0; }
  60% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes flow-fade {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
`

export function FlowMockupStyles() {
  return <style>{FLOW_MOCKUP_KEYFRAMES}</style>
}

function MicIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M6 11a6 6 0 0 0 12 0M12 17v4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CheckSvg() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12.5l4.5 4.5L19 7"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Waveform() {
  return (
    <span className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="bg-primary inline-block w-1.5 rounded-full"
          style={{
            height: 18,
            transformOrigin: 'center',
            animation: `flow-wave 0.9s ease-in-out ${i * 0.18}s infinite`,
          }}
        />
      ))}
    </span>
  )
}

const SPEAKING_WORDS = ['¡Mucho', 'gusto!']

// `marked` = how many words are highlighted so far (as if the learner reads
// them out loud one by one), so the phrase gets marked word-by-word.
const SPEAKING_PHASES = [
  { name: 'mic', duration: 1100, marked: 0 },
  { name: 'listening', duration: 1200, marked: 0 },
  { name: 'read-1', duration: 850, marked: 1 },
  { name: 'read-2', duration: 950, marked: 2 },
  { name: 'feedback', duration: 1600, marked: 2 },
] as const

export function SpeakingMockup() {
  const { t } = useTranslation()
  const [phaseIndex, setPhaseIndex] = useState(0)

  useEffect(() => {
    let index = 0
    let timer: ReturnType<typeof setTimeout>

    const schedule = () => {
      timer = setTimeout(() => {
        index = (index + 1) % SPEAKING_PHASES.length
        setPhaseIndex(index)
        schedule()
      }, SPEAKING_PHASES[index].duration)
    }

    schedule()
    return () => clearTimeout(timer)
  }, [])

  const phase = SPEAKING_PHASES[phaseIndex]
  const isMic = phase.name === 'mic'
  const isFeedback = phase.name === 'feedback'
  const isListening = !isMic && !isFeedback

  // A single fixed-size card and one morphing circle keep the box from
  // resizing between phases; only colors and inner content cross-fade so the
  // whole flow feels continuous.
  const circleColor = isListening ? 'text-primary' : 'bg-primary text-white'

  return (
    <div
      className="flex h-[220px] w-full max-w-[380px] flex-col items-center justify-center gap-7 rounded-3xl p-8 shadow-xl"
      style={{ backgroundColor: CARD }}
    >
      <div className="flex h-9 items-center justify-center">
        {isFeedback ? (
          <span
            key="feedback"
            className="text-xl font-bold"
            style={{ color: ACCENT, animation: 'flow-fade 0.4s ease-out' }}
          >
            {t('pages.flow.mockup.speakingPerfect')} 🎉
          </span>
        ) : (
          <span
            className="text-xl font-bold"
            style={{ color: isMic ? 'transparent' : ACCENT }}
          >
            {SPEAKING_WORDS.map((word, i) => (
              <span key={word}>
                {i > 0 ? ' ' : ''}
                <span
                  className="rounded px-1 py-0.5 transition-colors duration-300"
                  style={{
                    backgroundColor:
                      !isMic && i < phase.marked
                        ? 'rgba(90,169,255,0.28)'
                        : 'transparent',
                  }}
                >
                  {word}
                </span>
              </span>
            ))}
          </span>
        )}
      </div>

      <div
        className={`relative flex h-16 w-16 items-center justify-center rounded-full shadow-lg transition-colors duration-500 ${circleColor}`}
        style={isListening ? { backgroundColor: LIGHT } : undefined}
      >
        {isListening && (
          <span
            className="absolute inset-0 rounded-full"
            style={{
              backgroundColor: 'rgba(234,243,255,0.5)',
              animation: 'flow-pulse-ring 1.4s ease-out infinite',
            }}
          />
        )}

        <span
          key={phase.name}
          className="relative flex items-center justify-center"
          style={{ animation: 'flow-fade 0.35s ease-out both' }}
        >
          {isFeedback ? <CheckSvg /> : isListening ? <Waveform /> : <MicIcon />}
        </span>
      </div>
    </div>
  )
}

function FeedbackToolbarIcon({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-4 w-4 items-center justify-center text-white/70">
      {children}
    </span>
  )
}

export function FeedbackMockup() {
  const { t } = useTranslation()
  const meaning = t('pages.flow.mockup.feedback.translation')
  const answer = '¿Quién es él?'

  return (
    <div
      className="w-full max-w-[380px] rounded-3xl p-5 shadow-xl"
      style={{ backgroundColor: CARD }}
    >
      <div className="flex items-start gap-3">
        <div
          className="mt-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full"
          style={{ background: `conic-gradient(#006bff 306deg, ${PANEL} 0)` }}
        >
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: CARD }}
          >
            85
          </span>
        </div>

        <div
          className="bg-primary mt-2 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white"
          style={{ opacity: 0.9 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 20h4L18 10l-4-4L4 16v4z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div
          className="flex-1 rounded-2xl px-4 py-3 text-left text-white shadow-lg"
          style={{
            background:
              'linear-gradient(120deg,#006bff 0%,#2f8bff 55%,#37c0ff 100%)',
            animation: 'flow-float 3.5s ease-in-out infinite',
          }}
        >
          <p className="text-lg font-bold">
            ¿Quién <span className="text-[#ffcf5a]">a</span> él?
          </p>
          <p className="text-sm text-white/85">{meaning}</p>
          <div className="mt-2 flex gap-3">
            <FeedbackToolbarIcon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M5 9v6h4l5 4V5L9 9H5z" fill="currentColor" />
              </svg>
            </FeedbackToolbarIcon>
            <FeedbackToolbarIcon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 12h2m4-5v10m4-7v4m4-6v8m2-5h2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </FeedbackToolbarIcon>
            <FeedbackToolbarIcon>
              <span className="text-[10px] font-bold">文A</span>
            </FeedbackToolbarIcon>
            <FeedbackToolbarIcon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 5h16v11H8l-4 4V5z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </FeedbackToolbarIcon>
          </div>
        </div>
      </div>

      <div
        className="mt-3 rounded-2xl p-4"
        style={{
          backgroundColor: PANEL_DARK,
          animation: 'flow-slide-in 0.5s ease-out both',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12.5l4.5 4.5L19 7"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="text-sm text-white/60">
            {t('pages.flow.mockup.feedback.correctAnswer')}
          </span>
        </div>
        <p className="mt-1 text-lg font-bold text-white">{answer}</p>
        <div className="my-3 border-t border-white/10" />
        <p className="text-sm leading-relaxed text-white/70">
          {t('pages.flow.mockup.feedback.hint', { answer, meaning })}
        </p>
      </div>
    </div>
  )
}

function TypingDots() {
  return (
    <div
      className="flex w-fit gap-1.5 rounded-2xl px-4 py-3"
      style={{
        backgroundColor: PANEL,
        animation: 'flow-slide-in 0.4s ease-out both',
      }}
    >
      <span
        className="h-2 w-2 rounded-full bg-white/60"
        style={{ animation: 'flow-typing 1.2s ease-in-out infinite' }}
      />
      <span
        className="h-2 w-2 rounded-full bg-white/60"
        style={{ animation: 'flow-typing 1.2s ease-in-out 0.2s infinite' }}
      />
      <span
        className="h-2 w-2 rounded-full bg-white/60"
        style={{ animation: 'flow-typing 1.2s ease-in-out 0.4s infinite' }}
      />
    </div>
  )
}

const TUTOR_DURATIONS = [1100, 1600, 1000, 2000, 2600] as const

export function TutorMockup() {
  const { t } = useTranslation()
  const [phase, setPhase] = useState(0)

  useEffect(() => {
    let index = 0
    let timer: ReturnType<typeof setTimeout>

    const schedule = () => {
      timer = setTimeout(() => {
        index = (index + 1) % TUTOR_DURATIONS.length
        setPhase(index)
        schedule()
      }, TUTOR_DURATIONS[index])
    }

    schedule()
    return () => clearTimeout(timer)
  }, [])

  const showBubble1 = phase >= 1
  const showBubble2 = phase >= 3
  const showOptions = phase >= 4
  const showTyping = phase === 0 || phase === 2

  return (
    <div
      className="flex h-[360px] w-full max-w-[380px] flex-col rounded-3xl p-5 shadow-xl"
      style={{ backgroundColor: CARD }}
    >
      <div className="mb-3 flex items-center gap-2">
        <span
          className="h-5 w-5 rounded-full"
          style={{
            background: 'linear-gradient(135deg,#006bff,#37c0ff)',
          }}
        />
        <span className="text-xs font-semibold text-white/70">
          LingoChatAI {t('pages.flow.mockup.tutor.title')}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-hidden">
        {showBubble1 && (
          <div
            className="rounded-2xl p-3 text-[13px] leading-snug text-white/90"
            style={{
              backgroundColor: PANEL,
              animation: 'flow-slide-in 0.4s ease-out both',
            }}
          >
            {t('pages.flow.mockup.tutor.intro')}
          </div>
        )}

        {showBubble2 && (
          <div
            className="rounded-2xl p-3 text-[13px] leading-snug text-white/90"
            style={{
              backgroundColor: PANEL,
              animation: 'flow-slide-in 0.4s ease-out both',
            }}
          >
            <strong className="font-bold text-white">
              &ldquo;¡Hola, Karla! Gusto verte por fin en persona.&rdquo;
            </strong>{' '}
            {t('pages.flow.mockup.tutor.feedbackConnector')}{' '}
            <strong className="font-bold text-white">
              &ldquo;¡Hola, Karla! Me da gusto verte por fin en persona.&rdquo;
            </strong>
          </div>
        )}

        {showTyping && <TypingDots />}

        {showOptions && (
          <div
            className="mt-auto flex flex-col items-end gap-2"
            style={{ animation: 'flow-slide-in 0.4s ease-out both' }}
          >
            <span className="text-primary rounded-full bg-white px-4 py-2 text-[13px] font-semibold">
              {t('pages.flow.mockup.tutor.provideMoreFeedback')}
            </span>
            <span className="text-primary rounded-full bg-white px-4 py-2 text-[13px] font-semibold">
              {t('pages.flow.mockup.tutor.giveExample')}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
